/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import safeAreaInsets from 'safe-area-insets';
// import _cloneDeep from 'lodash/cloneDeep';
import _forEach from 'lodash/forEach';
// import _isString from 'lodash/isString';
// import _isDate from 'lodash/isDate';
// import _isFunction from 'lodash/isFunction';
// import _isArray from 'lodash/isArray';
import underscore from 'underscore';
import { getTuValue } from './aws/utils/getTuValue';
import { replaceAll } from './utils/replaceAll.polyfill';
import { setKeyValueInLocalStorage } from './localstorage/setKeyValueInLocalStorage';
import { saveToLocalStorage } from './localstorage/saveToLocalStorage';
import HelpBoxConfig from '../components/HelpBox/HelpBox.config';
import SunCalc from '../lib/suncalc/suncalc';
import auditDataService from './auditData.service';
// import bootstrapAppService from "./bootstrapApp.service";
import applicationDataSource from '../datasource/applicationdata.datasource';
import d3 from '../globalexports/d3.export';
import { getKeyValueFromLocalStorage } from './localstorage/getKeyValueFromLocalStorage';
import capacitorService from './capacitor.service';
import ChartConst from '../constants/chart.constant';
import DefConst from '../constants/def.constant';
import AppConst from '../constants/app.constant';
// eslint-disable-next-line import/no-cycle
import ConfigDeviceSettings from '../config/device.config';
// eslint-disable-next-line import/no-cycle
import rulesetService from './ruleset.service';
import * as AltitudeOptionsConst from '../constants/altitude.constant';

const utilityService = () => {
  const contents = {};
  contents.safeAreaCalculated = null;
  // from http://stackoverflow.com/a/27939662/1247602
  const toRad = function (deg) {
    return deg * (Math.PI / 180);
  };
  const toDeg = function (rad) {
    return rad * (180 / Math.PI);
  };
  const geo = {
    bearing(lat1, lng1, lat2, lng2) {
      const dLon = toRad(lng2 - lng1);
      const y = Math.sin(dLon) * Math.cos(toRad(lat2));
      const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
        - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
      const brng = toDeg(Math.atan2(y, x));
      return (brng + 360) % 360;
    },
    distance(lat1, lng1, lat2, lng2) {
      // ref: http://www.movable-type.co.uk/scripts/latlong.html
      const R = 6371e3;
      const radLat1 = toRad(lat1);
      const radLat2 = toRad(lat2);
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lng2 - lng1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
  };

  function getUrlParameter(name) {
    // eslint-disable-next-line no-useless-escape
    name = name.replace(/[\[]/, "\\['").replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
    const results = regex.exec(window.location.href);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }
  contents.getRulesetValue = () => {
    let rulesetValue = AppConst.defaultRulesetKey;
    let tmpUrlParam;
    if (ConfigDeviceSettings.settings.enableAlternateColorScheme) {
      rulesetValue = AppConst.alternateColorSchemeRuleKey;
      tmpUrlParam = getUrlParameter('ruleset');
      if (tmpUrlParam && DefConst.factoryRuleSetMap[tmpUrlParam]) {
        rulesetValue = tmpUrlParam;
      }
      return rulesetValue;
    }
    return rulesetValue;
  };
  contents.validateEmail = function (email) {
    // eslint-disable-next-line no-useless-escape
    const re = /^(([^<>()\[\]\\.,;:\s@\"]+(\.[^<>()\[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };
  contents.loadRulesetInfo = () => {
    rulesetService.loadRulesetConfiguration(contents.getRulesetValue());
  };
  contents.getSelfElement = function (args) {
    return args[2][args[1]];
  };
  contents.subtractMinutes = function (date, minutes) {
    const newDate = date.setTime(date.getTime() - minutes * 60000);
    return new Date(newDate);
  };
  contents.addMinutes = function (date, minutes) {
    const newDate = date.setTime(date.getTime() + minutes * 60000);
    return new Date(newDate);
  };
  contents.addMonth = function (date, month) {
    const newDate = date.setMonth(date.getMonth() + month);
    return new Date(newDate);
  };
  contents.isDeviceHavingUTCTimezone = () => {
    const timeZoneOffset = new Date().getTimezoneOffset();
    return !timeZoneOffset;
  };
  contents.getEnvString = () => {
    const envString = '';
    if (contents.isCordovaApplication()) {
      // if (bootstrapAppService && bootstrapAppService.isDebugMode) {
      //     envString += "DEBUG:";
      // }
      if (!!window.TFDetect && window.TFDetect.isTFXC) {
        return `${envString}TEST FLIGHT / XC`;
      }
      if (capacitorService.isAndroid) {
        return `${envString}ANDROID`;
      }
      return `${envString}APP STORE`;
    }
    return `${envString}WEBSITE`;
  };
  contents.convertMinsToHrsMins = function (minutes) {
    let h = Math.floor(minutes / 60);
    let m = minutes % 60;
    h = h < 10 ? `0${h}` : h;
    m = m < 10 ? `0${m}` : m;
    return `${h}:${m}`;
  };
  contents.addHours = function (date, hours) {
    const newDate = date.setTime(date.getTime() + hours * 60 * 60 * 1000);
    return new Date(newDate);
  };

  contents.subtractHours = function (date, hours) {
    const newDate = date.setTime(date.getTime() - hours * 60 * 60 * 1000);
    return new Date(newDate);
  };

  contents.isValueBetweenRange = function (value, min, max) {
    return value > min && value < max;
  };
  contents.getSettingValue = function (currentValue, defaultValue) {
    let settingValue;
    if (currentValue === undefined || currentValue === null) {
      settingValue = defaultValue;
    } else {
      settingValue = currentValue;
    }
    return settingValue;
  };

  function removeDuplicatesByKeyFromObject(obj, key) {
    const hashesFound = {};
    let results;

    function hash(o, keyName) {
      return o[keyName];
    }
    if (obj) {
      obj.forEach((o) => {
        hashesFound[hash(o, key)] = o;
      });
      results = Object.keys(hashesFound).map((k) => hashesFound[k]);
    }
    return results || obj;
  }
  contents.removeDuplicatesByKeyFromObjectInterface = function (obj, key) {
    return removeDuplicatesByKeyFromObject(obj, key);
  };
  contents.getTimeElapsedInMiliseconds = function (dateValue) {
    const today = new Date();
    const diffMs = today - dateValue;
    return diffMs;
  };
  contents.daysDifferenceBetweenDates = function (first, second) {
    return Math.round((second - first) / (1000 * 60 * 60 * 24));
  };
  contents.isCordovaApplication = () => capacitorService.isCapacitorMobileApp;
  contents.isIOS = () => capacitorService.isIos;
  contents.isAndroid = () => capacitorService.isAndroid;
  contents.isIphoneXPortrait = () => {
    if (contents.isIOS() && window.innerHeight === 812 && window.innerWidth === 375) {
      return false;
    }
    return false;
  };
  contents.getAvailableSpaceWorkshop = () => {
    const footerHeight = contents.iPhoneXFooterExtraOffset();
    const availableHeight = window.innerHeight - contents.getHeaderHeight() - footerHeight;
    const availableWidth = window.innerWidth;
    return {
      height: availableHeight,
      width: availableWidth,
    };
  };
  contents.getAvailableSpaceAirport = function (isMobileView) {
    const footerHeight = contents.getFooterHeight() + contents.iPhoneXFooterExtraOffset();
    const availableHeight = window.innerHeight - contents.getHeaderHeight() - footerHeight;
    const availableWidth = window.innerWidth - (isMobileView ? 0 : AppConst.sideMenuWidth);
    return {
      height: availableHeight,
      width: availableWidth,
    };
  };
  contents.getImageryAvailableSpace = function (numberOfImages, isSubNav, isMobileView) {
    const hideBottomAxis = contents.hideBottomAxis();
    let footerHeight = contents.getFooterHeight() + contents.iPhoneXFooterExtraOffset();
    const subheaderHeight = AppConst.headerHeight;
    let sliderHeight = contents.getSliderHeight();
    // sliderHeight = 0;
    // console.log("asdasdasd");
    if (numberOfImages <= 1) {
      sliderHeight = 0;
    }
    // var verticalSlider
    if (hideBottomAxis) {
      sliderHeight = 0;
      footerHeight = 0;
    }
    let availableHeight = window.innerHeight
      - contents.getHeaderHeight()
      - footerHeight
      - subheaderHeight
      - sliderHeight;
    let availableWidth = window.innerWidth
      - (isSubNav ? 0 : AppConst.imageryAccordWidth)
      - (isMobileView ? 0 : AppConst.sideMenuWidth);
    if (hideBottomAxis) {
      availableWidth -= AppConst.sliderHeightSecondary;
      if (numberOfImages <= 1) {
        // if there is only single image then we dont have any sider
        availableWidth += AppConst.sliderHeightSecondary;
      }
      availableWidth -= subheaderHeight;
      availableHeight -= 4;
      availableHeight += subheaderHeight;
    }
    return {
      height: availableHeight,
      width: availableWidth,
    };
  };
  contents.getSafeAreaPadding = () => safeAreaInsets;
  contents.getHeaderHeight = () => {
    contents.getSafeAreaPadding();
    return AppConst.headerHeight + safeAreaInsets.top;
  };
  contents.useSubNavForImagery = () => {
    if (contents.is_iphone()) {
      return true;
    }
    if (contents.is_ipad()) {
      if (contents.getOrientation() === ChartConst.orientation.landscape) {
        return false;
      }
      return true;
    }
    return false;
  };
  contents.isSafari = () => {
    let isSafari = navigator.userAgent.indexOf('Safari') > -1;
    const isChrome = navigator.userAgent.indexOf('Chrome') > -1;
    if (isChrome && isSafari) {
      isSafari = false;
    }
    return isSafari;
  };
  contents.isFirefox = () => {
    if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
      return true;
    }
    return false;
  };

  contents.getAdditionShiftToHandleWalthrough = function (holeTopShift) {
    if (capacitorService.isIos >= 11) {
      holeTopShift = false;
    }
    if (typeof holeTopShift === 'string') {
      holeTopShift = holeTopShift === 'true';
    }
    if (holeTopShift && (contents.isSafari() || capacitorService.isIos)) {
      return contents.getHeaderHeight();
    }
    return 0;
  };
  contents.getAdditionShiftToHandleWalthroughForHtml = function (innerHtmlElement) {
    if (innerHtmlElement) {
      return contents.getHeaderHeight();
    }
    return 0;
  };

  contents.iPhoneXFooterExtraOffset = () => {
    if (contents.isIphoneXPortrait()) {
      return AppConst.footerHeightExtraiPhoneX;
    }
    return 0;
  };

  contents.getFooterHeight = function (dontHaveSlider) {
    contents.getSafeAreaPadding();
    if (dontHaveSlider) {
      return contents.hideBottomAxis()
        ? safeAreaInsets.bottom
        : AppConst.footerHeight + safeAreaInsets.bottom;
    }
    return contents.hideBottomAxis() || ConfigDeviceSettings.settings.enableAlternateSlider
      ? safeAreaInsets.bottom
      : AppConst.footerHeight + safeAreaInsets.bottom;
  };
  contents.sortByForecastTs = function (a, b) {
    return new Date(a.forecast_ts).getTime() - new Date(b.forecast_ts).getTime();
  };
  contents.is_touch_device = () => Object.prototype.hasOwnProperty.call(window, 'ontouchstart')
    || navigator.MaxTouchPoints > 0
    || navigator.msMaxTouchPoints > 0;
  contents.is_ipad = () => window.innerWidth >= 813 && window.innerWidth <= 1025;
  contents.is_iphone = () => window.innerWidth >= 100 && window.innerWidth <= 798;
  contents.useShortAirportSelector = () => window.innerWidth >= 100 && window.innerWidth <= 500;
  contents.isModalFullScreen = () => {
    if (window.innerHeight >= 600 && window.innerWidth >= 768) {
      return false;
    }
    return true;
  };
  contents.is_small_device = () => window.innerWidth >= 100 && window.innerWidth <= 1200;
  contents.checkIsIphone5Size = () => window.innerWidth >= 320 && window.innerWidth <= 330;
  contents.showUtcClock = () => window.innerWidth > 330;
  contents.is_big_device = () => window.innerWidth >= 1200;
  contents.isShowingSideMenu = () => window.innerWidth >= 1200;
  contents.getBarbPadding = () => {
    let defaultWindSpeed;
    if (contents.is_iphone()) {
      defaultWindSpeed = ChartConst.windSpeed.mobileLight;
    } else if (contents.is_ipad()) {
      defaultWindSpeed = ChartConst.windSpeed.tabletLight;
    } else {
      defaultWindSpeed = ChartConst.windSpeed.light;
    }
    return defaultWindSpeed.arrowWidth / 2 + 4;
  };
  contents.getOrientation = () => {
    let orientation = '';
    if (window.matchMedia('(orientation: portrait)').matches) {
      orientation = ChartConst.orientation.portrait;
    }
    if (window.matchMedia('(orientation: landscape)').matches) {
      orientation = ChartConst.orientation.landscape;
    }
    return orientation;
  };
  contents.sortByKey = function (array, key) {
    return array.sort((a, b) => {
      const x = a[key];
      const y = b[key];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  };
  contents.findMaxMinValue = function (findMax, list) {
    if (findMax) {
      return Math.max.apply(
        null,
        list.values.map((o) => o.y),
      );
    }
    return Math.min.apply(
      null,
      list.values.map((o) => o.y),
    );
  };
  contents.findMinPositiveNumber = function (collection) {
    let i = 0;
    let value;
    while (i < collection.length) {
      if (collection[i] > 0) {
        value = collection[i];
        break;
      }
      i += 1;
    }
    return value - ChartConst.offsetForMin < 0 ? value : value - ChartConst.offsetForMin;
  };
  contents.getCloudText = function (cld) {
    switch (cld) {
      case 'CL':
        return 'CLR';
      case 'FW':
        return 'FEW';
      case 'SC':
        return 'SCT';
      case 'BK':
        return 'BKN';
      case 'OV':
        return 'OVC';
      case 'OVX':
        return 'OVX';
      default:
        return cld;
    }
  };
  contents.degreeToRadian = function (degrees) {
    return degrees * (Math.PI / 180);
  };
  contents.nauticalMileToMiles = function (nauticalMile) {
    return nauticalMile * 1.15078;
  };
  contents.knotsToMilePerHour = function (knots) {
    return knots * 1.15078;
  };
  contents.mphToKnots = function (mph) {
    let knots;
    if (mph && !Number.isNaN(Number(mph))) {
      knots = mph * 0.86897624190816;
      return knots.toFixed(0);
    }
    return null;
  };
  contents.mpsToKnots = function (mps) {
    return mps * 1.943844;
  };
  contents.meterToFeet = function (mtr) {
    return mtr * 3.2808;
  };
  contents.fahrenheitToCelsius = function (fahrenheitToCelsius) {
    // If fahrenheit is null then return celsius as null
    return contents.isValueValid(fahrenheitToCelsius) ? (fahrenheitToCelsius - 32) * (5 / 9) : null;
  };
  contents.celsiusToFahrenheit = function (celsiusToFahrenheit) {
    // If fahrenheit is null then return celsius as null
    return contents.isValueValid(celsiusToFahrenheit) ? celsiusToFahrenheit * (9 / 5) + 32 : null;
  };
  contents.getAloftWindDirection = function (UGRD, VGRD) {
    return ((270 - Math.atan2(VGRD, UGRD) * 57.29578) % 360) / 10;
  };
  contents.getAloftWindSpeed = function (UGRD, VGRD) {
    return contents.mpsToKnots(Math.sqrt(UGRD * UGRD + VGRD * VGRD));
  };
  contents.getTimeFormat = function (timeZone) {
    if (timeZone === AppConst.timeZones.local.key) {
      return d3.timeFormat(
        ChartConst.GraphConfig.xAxis.format + ChartConst.GraphConfig.xAxis.timePostFix.local,
      );
    }
    return d3.utcFormat(
      ChartConst.GraphConfig.xAxis.format + ChartConst.GraphConfig.xAxis.timePostFix.zulu,
    );
  };
  contents.covertEpochToDate = function (timeZone, d) {
    if (d) {
      if (timeZone === AppConst.timeZones.local.key) {
        return d3.timeFormat('%H')(d);
      }
      return d3.utcFormat('%H')(d);
    }
    return null;
  };
  contents.getUnregisterdUserPurchase = () => JSON.parse(
    localStorage.getItem(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.unregisteredUser,
    ),
  );
  contents.setUnregisterdUserPurchase = function (data) {
    saveToLocalStorage(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.unregisteredUser,
      JSON.stringify(data),
    );
  };
  contents.removeUnregisterdUserPurchase = () => {
    // localStorage.removeItem(AppConst.localstorageConf.prefix + AppConst.localstorageConf
    //   .unregisteredUser);
  };
  contents.getSuppressedMetFromLocalStorage = () => {
    const suppressedMet = localStorage.getItem(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.suppressedMets,
    );
    if (!suppressedMet) {
      return ChartConst.defaultSuppressedMets;
    }
    return JSON.parse(
      localStorage.getItem(
        AppConst.localstorageConf.prefix + AppConst.localstorageConf.suppressedMets,
      ),
    );
  };
  contents.setSuppressedMetLocalStorage = function (data) {
    saveToLocalStorage(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.suppressedMets,
      JSON.stringify(data),
    );
  };
  contents.setFavoriteInLocalStorage = function (data) {
    saveToLocalStorage(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.favoriteList,
      JSON.stringify(data),
    );
  };
  contents.setSubscriptionExpireReminderInLocalStorage = function (data) {
    setKeyValueInLocalStorage(AppConst.localstorageConf.subscriptionExpireSoonReminder, data);
  };
  contents.getSubscriptionExpireReminderFromLocalStorage = function () {
    return getKeyValueFromLocalStorage(AppConst.localstorageConf.subscriptionExpireSoonReminder);
  };
  contents.removeSubscriptionExpireReminderFromLocalStorage = function () {
    contents.removeKeyValueFromLocalStorage(
      AppConst.localstorageConf.subscriptionExpireSoonReminder,
    );
  };
  contents.setWeatherApiCacheLocalStorage = function (data) {
    saveToLocalStorage(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.sevenDayWeatherApiUrlsMapping,
      JSON.stringify(data),
    );
  };
  contents.getWeatherApiCacheLocalStorage = () => JSON.parse(
    localStorage.getItem(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.sevenDayWeatherApiUrlsMapping,
    ),
  );
  contents.setForgotPasswordState = (value) => {
    if (value) {
      saveToLocalStorage(
        AppConst.localstorageConf.prefix + AppConst.localstorageConf.forgotPwdState,
        JSON.stringify(value),
      );
    }
  };
  contents.getForgotPasswordState = () => JSON.parse(
    localStorage.getItem(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.forgotPwdState,
    ),
  );
  contents.deleteForgotPasswordState = () => {
    // localStorage.removeItem(AppConst.localstorageConf.prefix + AppConst.localstorageConf
    //   .forgotPwdState);
  };
  contents.removeKeyValueFromLocalStorage = (key) => {
    if (key) {
      // localStorage.removeItem(AppConst.localstorageConf.prefix + key);
    }
  };
  contents.setFullDataInLocalStorage = function (data, datamode) {
    if (data) {
      saveToLocalStorage(
        `${AppConst.localstorageConf.prefix + AppConst.localstorageConf.fullDataKey}_${datamode}`,
        JSON.stringify(data),
      );
    }
  };
  contents.setSettingInLocalstorage = function (settings, settingKey) {
    if (settings) {
      if (settingKey === 'weatherSetting' && settings.airportDetails) {
        // No need to save tabs data for weather 7day screen
        settings = contents.clone(settings, true);
        delete settings.airportDetails.tabs;
      }
      saveToLocalStorage(AppConst.localstorageConf.prefix + settingKey, JSON.stringify(settings));
    }
  };
  contents.setAppInstallDate = () => {
    if (!contents.getAppInstallDate()) {
      const tu = new Date().toISOString();
      setKeyValueInLocalStorage(AppConst.localstorageConf.tu, tu);
    }
  };
  contents.getAppInstallDate = () => {
    const appInstallDate = localStorage.getItem(
      AppConst.localstorageConf.prefix + AppConst.localstorageConf.tu,
    );
    return appInstallDate;
  };
  contents.setServiceHostPreference = function (servicehost) {
    if (servicehost) {
      saveToLocalStorage(
        AppConst.localstorageConf.prefix + AppConst.localstorageConf.serviceHost,
        servicehost,
      );
    }
  };
  contents.removeServiceHostPreference = function () {
    // localStorage.removeItem(AppConst.localstorageConf.prefix + AppConst.localstorageConf
    //   .serviceHost);
  };
  contents.getSettingFromLocalstorage = function (settingKey) {
    return JSON.parse(localStorage.getItem(AppConst.localstorageConf.prefix + settingKey));
  };
  contents.sortByDate = function (a, b) {
    return new Date(b.logDate).getTime() - new Date(a.logDate).getTime();
  };

  contents.formatDateMMDDYYYYHHMM = function (date) {
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    const strTime = `${hours}:${minutes} ${ampm}`;
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}  ${strTime}`;
  };
  contents.getDepartureTime = function (leftSideAnchor, pivot, maxValueOfSlider) {
    let lastEdt = getKeyValueFromLocalStorage(AppConst.localstorageConf.lastEdt);
    if (lastEdt) {
      lastEdt = new Date(lastEdt);
    }
    let currentDate = new Date();
    if (pivot) {
      currentDate = pivot;
    }
    if (lastEdt && lastEdt > currentDate && lastEdt <= maxValueOfSlider) {
      currentDate = lastEdt;
    }
    const minutesLag = currentDate.getMinutes() % parseInt(leftSideAnchor, 10);
    currentDate = contents.subtractMinutes(currentDate, minutesLag);
    return currentDate;
  };
  contents.getAllDatesInNextXMinutes = function (
    x,
    mavEndsAt,
    flightDurationMinutes,
    leftAnchor,
    startLampFrom,
    shouldSendAllRange,
  ) {
    // AppConst.leftSideAnchor
    let currentDate;
    if (startLampFrom) {
      currentDate = startLampFrom;
    } else {
      currentDate = new Date();
    }
    const allRange = [];
    const minutesLag = currentDate.getMinutes() % parseInt(leftAnchor, 10);
    currentDate = contents.subtractMinutes(currentDate, minutesLag);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);
    const dates = [];
    let i = 0;
    let dateToPush;
    // Subtract flightDurationMinutes by mavEndsAt
    const maxRangeDate = contents.subtractHours(new Date(mavEndsAt), flightDurationMinutes);
    while (i <= x) {
      dateToPush = contents.addMinutes(new Date(currentDate), i);
      if (dateToPush <= maxRangeDate) {
        dates.push(dateToPush);
      }
      allRange.push(dateToPush);
      i += parseInt(leftAnchor, 10);
    }
    if (shouldSendAllRange) {
      return allRange;
    }
    return dates;
  };
  contents.loadAloftAltitudeMap = function (aloftCloudIndexMap, dataMode) {
    const source = aloftCloudIndexMap;
    const altMap = [];
    let i = 1;
    let value;
    while (i < source.length) {
      value = source[i - 1] + (source[i] - source[i - 1]) / 2;
      if (!Number.isNaN(Number(value))) {
        altMap.push(value);
      }
      i += 1;
    }
    DefConst[`aloftAltitudeMap${dataMode}`] = altMap;
  };
  contents.getDateAfterAddingNminutes = function (date, x) {
    return contents.addMinutes(new Date(date), x);
  };

  contents.getWeatherFilePath = function (filename, useWhite) {
    return (
      ChartConst.weatherIconsDirectory
      + (useWhite ? ChartConst.whiteWeatherIconsDirectory : '')
      + filename
      + ChartConst.weatherFileFormat
    );
  };

  contents.showAbbreviatedYTicks = () => {
    if (contents.is_small_device()) {
      return true;
    }
    return false;
  };

  contents.appendAglMsl = function (isAglAirmet, number, aglMslText) {
    if (!isAglAirmet && number >= AppConst.flightLevelAltitude) {
      return '';
    }
    return aglMslText;
  };
  contents.abbreviateCeiling = function (number, postfix, isAglAirmet, hazardType, altType) {
    if (number === null && altType === 'min') {
      number = 0;
    }
    if (hazardType === ChartConst.gairmetConfig.icing.key && number === -1) {
      number = 'FZLVL';
    } else if (!isAglAirmet && number <= 0) {
      number = AppConst.surface;
    } else if (!isAglAirmet && number === AppConst.flightLevelAltitude) {
      number = AppConst.flightLevel + 180;
    } else if (!isAglAirmet && number > AppConst.flightLevelAltitude) {
      // For CONVECTIVE
      if (
        hazardType === ChartConst.gairmetConfig.convectiveAirmet.key
        && number >= ChartConst.convectiveMaxThreshold
      ) {
        number = ChartConst.convectiveMaxThresholdDisplay;
      } else {
        number = AppConst.flightLevel + (number / 100 > 0 ? number / 100 : '');
      }
    } else if (number >= 1000) {
      number /= 1000;
      number = number.toFixed(0) + postfix;
    }
    return number;
  };
  contents.abbreviateAltitudeCeiling = function (number, postfix) {
    if (number >= AppConst.flightLevelAltitude) {
      number /= 100;
      number = AppConst.flightLevel + number.toFixed(0);
    } else if (number >= 1000) {
      number /= 1000;
      if (number > 9) {
        number = number.toFixed(0) + postfix;
      } else {
        number = number.toFixed(1);
      }
    }
    if (number === '18K') {
      // If due to rounding it is showing 18k then it means FL180
      number = AppConst.flightLevel + 180;
    }
    return number;
  };
  contents.getPlaneDirection = (p1, p2) => {
    if (p1 && p2) {
      return geo.bearing(p1.lat, p1.lng, p2.lat, p2.lng);
    }
    return 0;
  };
  contents.distanceBetweenPoint = (p1, p2) => geo.distance(p1.lat, p1.lng, p2.lat, p2.lng);
  // contents.hideBottomAxis = () => true;
  contents.hideBottomAxis = () => contents.getOrientation() === ChartConst.orientation.landscape
    && contents.isCordovaApplication()
    && window.innerWidth >= 100
    && window.innerWidth < 1024
    && window.innerHeight < 768;

  contents.getTransformationNonSvg = function (transform) {
    const regExp = /\(([^)]+)\)/;
    if (transform) {
      const matches = regExp.exec(transform);
      if (matches && matches.length) {
        return parseFloat(matches[1]);
      }
    }
    return null;
  };

  contents.getTransformation = function (transform) {
    let g;
    if (typeof transform === 'object') {
      g = transform;
    } else {
      g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttributeNS(null, 'transform', transform);
    }
    if (g.transform.baseVal.consolidate()) {
      const { matrix } = g.transform.baseVal.consolidate();
      let { a } = matrix;
      let { b } = matrix;
      let { c } = matrix;
      let { d } = matrix;
      const { e } = matrix;
      const { f } = matrix;
      let scaleX = Math.sqrt(a * a + b * b);
      const scaleY = Math.sqrt(c * c + d * d);
      let skewX = a * c + b * d;
      if (scaleX) {
        a /= scaleX;
        b /= scaleX;
      }
      if (skewX) {
        c -= a * skewX;
        d -= b * skewX;
      }
      if (scaleY) {
        c /= scaleY;
        d /= scaleY;
        skewX /= scaleY;
      }
      if (a * d < b * c) {
        a = -a;
        b = -b;
        skewX = -skewX;
        scaleX = -scaleX;
      }
      return {
        translateX: e,
        translateY: f,
        rotate: Math.atan2(b, a) * (Math.PI / 180),
        skewX: Math.atan(skewX) * (Math.PI / 180),
        scaleX,
        scaleY,
      };
    }
    return {
      translateX: 0,
      translateY: 0,
      rotate: 0,
      skewX: 0,
      scaleX: 0,
      scaleY: 0,
    };
  };

  contents.getSunMoonCalculation = function (atWhatTime, latitude, longitude) {
    const atWhatTimeMain = new Date(atWhatTime).setHours(12, 0, 0);
    // Setting hours as middle of day so that it gives correct snrise and sunset dates
    const times = SunCalc.getTimes(atWhatTimeMain, latitude, longitude);
    // format sunrise time from the Date object
    // var sunriseStr = times.sunrise.getHours() + ":" + times.sunrise.getMinutes();

    // get position of the sun (azimuth and altitude) at today's sunrise
    // var sunPos = SunCalc.getPosition(times.sunrise, latitude, longitude);

    // var moonPos = SunCalc.getMoonPosition(times.sunrise, latitude, longitude);

    // get sunrise azimuth in degrees
    // var sunriseAzimuth = sunPos.azimuth * 180 / Math.PI;
    return {
      todaysSunlightTime: times,
      // sunPos: sunPos,
      // sunriseAzimuth: sunriseAzimuth,
      // moonPos: moonPos,
      // sunriseStr: sunriseStr
    };
  };

  // This function is to handle area series line shift
  // Ref meteogramChart.directive
  contents.getAdditionalShiftToHandleAreaSeries = (key) => (key === ChartConst.ChartLabelSpecs.cig.key
    ? ChartConst.areaLineShift.invert
    : ChartConst.areaLineShift.normal);

  contents.debounceProcess = () => {
    // To call this function pass a callback function
    // And time in millisecond till which debounce is to be done
    let promise = null;
    return function (callback, ms) {
      clearTimeout(promise); // clearTimeout(timer);
      promise = setTimeout(callback, ms); // timer = setTimeout(callback, ms);
    };
  };
  contents.airportJSONProcessedList = {};
  contents.processAirportJson = () => {
    let i = 0;
    let j;
    const airportList = applicationDataSource.airportsListing;
    if (airportList) {
      while (i < airportList.length) {
        if (airportList[i].tabs) {
          j = 0;
          while (j < airportList[i].tabs.length) {
            airportList[i].tabs[j].text = airportList[i].tabs[j].t;
            airportList[i].tabs[j].key = airportList[i].tabs[j].k;
            airportList[i].tabs[j].param = airportList[i].tabs[j].p;
            j += 1;
          }
        }
        contents.airportJSONProcessedList[airportList[i].icao_id] = airportList[i];
        i += 1;
      }
    }
  };

  contents.getMatchedAirportObject = function (airportKey) {
    if (airportKey) {
      airportKey = airportKey.toUpperCase();
    }
    return contents.airportJSONProcessedList[airportKey] || null;
  };
  contents.hasValidAirportSelected = function (
    airportString,
    formViewSettings,
    selectedAirportKey,
  ) {
    let hasValidAirport = false;
    if (
      formViewSettings[selectedAirportKey]
      && formViewSettings[selectedAirportKey].originalObject.icao_id !== ''
    ) {
      hasValidAirport = true;
    } else if (airportString) {
      const matchedAirport = contents.getMatchedAirportObject(airportString.toUpperCase());
      if (matchedAirport) {
        formViewSettings[selectedAirportKey] = {
          originalObject: matchedAirport,
        };
        hasValidAirport = true;
      }
    }
    return hasValidAirport;
  };
  contents.hasValidStartAirport = function (start) {
    const startDetail = contents.getMatchedAirportObject(start);
    let isValidRoute = true;
    let bnMessage = 0;
    if (!startDetail) {
      bnMessage = 11;
      isValidRoute = false;
    }
    return {
      isValidRoute,
      bnMessage,
    };
  };
  contents.hasValidRoute = function (start, mid1, mid2, end) {
    const { disableRouteValidation } = ConfigDeviceSettings.settings;
    const startDetail = contents.getMatchedAirportObject(start.icao_id);
    const mid1Detail = contents.getMatchedAirportObject(mid1.icao_id);
    const mid2Detail = contents.getMatchedAirportObject(mid2.icao_id);
    const endDetail = contents.getMatchedAirportObject(end.icao_id);
    if (mid1.icao_id && !mid1Detail) {
      mid1.isInvalid = true;
    }
    if (mid2.icao_id && !mid2Detail) {
      mid2.isInvalid = true;
    }
    let isValidRoute = true;
    let bnMessage = 0;
    const dirtyId = [];
    const dirtyReasons = [];
    // let distStartMid1;
    // let distStartMid2;
    // let distStartEnd;
    if (startDetail && endDetail && (mid1Detail || mid2Detail) && disableRouteValidation !== true) {
      // distStartEnd = contents.distanceBetweenPoint(startDetail, endDetail);
      // if (mid1Detail) {
      //   distStartMid1 = contents.distanceBetweenPoint(startDetail, mid1Detail);
      // }
      // if (mid2Detail) {
      //   distStartMid2 = contents.distanceBetweenPoint(startDetail, mid2Detail);
      // }
      // This logic is to make sure only short route is decidedc
      // if (mid1Detail && mid2Detail && (distStartMid1 > distStartMid2) && (distStartMid2
      //     > distStartEnd)) {
      //   isValidRoute = false;
      //   bnMessage = 1;
      // } else if (mid1Detail && mid2Detail && (distStartMid1 > distStartMid2)) {
      //   isValidRoute = false;
      //   bnMessage = 2;
      // } else if (mid1Detail && mid2Detail && (distStartMid2 > distStartEnd)) {
      //   isValidRoute = false;
      //   bnMessage = 3;
      // } else if (mid1Detail && !mid2Detail && (distStartMid1 > distStartEnd)) {
      //   bnMessage = 4;
      //   isValidRoute = false;
      // } else if (!mid1Detail && mid2Detail && (distStartMid2 > distStartEnd)) {
      //   bnMessage = 5;
      //   isValidRoute = false;
      // } else
      if (mid1Detail && mid2Detail && mid1Detail.icao_id === mid2Detail.icao_id) {
        bnMessage = 13;
        isValidRoute = false;
      }
    } else if (!startDetail && !endDetail) {
      bnMessage = 6;
      isValidRoute = false;
    } else if (!startDetail && endDetail) {
      bnMessage = 7;
      isValidRoute = false;
    } else if (startDetail && !endDetail) {
      bnMessage = 8;
      isValidRoute = false;
    } else if (mid1 && mid1.isInvalid === true) {
      bnMessage = 9;
      isValidRoute = false;
    } else if (mid2 && mid2.isInvalid === true) {
      bnMessage = 10;
      isValidRoute = false;
    }
    if (startDetail && endDetail && startDetail.icao_id === endDetail.icao_id) {
      dirtyReasons.push(1);
      isValidRoute = false;
      dirtyId.push('destination');
    }
    if (startDetail && mid1Detail && startDetail.icao_id === mid1Detail.icao_id) {
      dirtyReasons.push(2);
      isValidRoute = false;
      dirtyId.push('mid1');
    }
    if (startDetail && mid2Detail && startDetail.icao_id === mid2Detail.icao_id) {
      dirtyReasons.push(3);
      isValidRoute = false;
      dirtyId.push('mid2');
    }
    if (mid1Detail && mid2Detail && mid1Detail.icao_id === mid2Detail.icao_id) {
      dirtyReasons.push(4);
      isValidRoute = false;
      dirtyId.push('mid2');
    }
    if (mid1Detail && endDetail && mid1Detail.icao_id === endDetail.icao_id) {
      dirtyReasons.push(5);
      isValidRoute = false;
      dirtyId.push('destination');
    }
    if (mid2Detail && endDetail && mid2Detail.icao_id === endDetail.icao_id) {
      dirtyReasons.push(6);
      isValidRoute = false;
      dirtyId.push('destination');
    }
    return {
      isValidRoute,
      bnMessage,
      dirtyId,
      dirtyReasons,
    };
  };
  contents.checkIfMakeAloftReq = function (lat, lng) {
    // longitude must be between -66 and -126 and the latitude must be between 24 and 50.
    if (
      lng >= DefConst.aloftBoundBox.leftLongitude
      && lng <= DefConst.aloftBoundBox.rightLongitude
      && lat >= DefConst.aloftBoundBox.bottomLatitude
      && lat <= DefConst.aloftBoundBox.topLatitude
    ) {
      return true;
    }
    return false;
  };
  contents.validateRoute = function (formViewSettings, invalidRouteObj, validateOnlyDeparture) {
    if (formViewSettings) {
      if (!validateOnlyDeparture) {
        invalidRouteObj = contents.hasValidRoute(
          formViewSettings.selectedDepartureAirport,
          formViewSettings.selectedMid1Airport,
          formViewSettings.selectedMid2Airport,
          formViewSettings.selectedDestinationAirport,
        );
      } else {
        // This section if for validating meteogram and 7day form
        invalidRouteObj = contents.hasValidStartAirport(
          formViewSettings.selectedDepartureAirport.icao_id,
        );
      }
      // switch (invalidRouteObj.bnMessage) {
      // case 1:
      //     formViewSettings.selectedMid1Airport.isinvalid = true;
      //     formViewSettings.selectedMid2Airport.isinvalid = true;
      //     break;
      // case 2:
      //     formViewSettings.selectedMid1Airport.isinvalid = true;
      //     break;
      // case 3:
      //     formViewSettings.selectedMid2Airport.isinvalid = true;
      //     break;
      // case 4:
      //     formViewSettings.selectedMid1Airport.isinvalid = true;
      //     break;
      // case 5:
      //     formViewSettings.selectedMid2Airport.isinvalid = true;
      //     break;
      // }
    }
    // console.log(formViewSettings);
    invalidRouteObj.formViewSettings = formViewSettings;
    return invalidRouteObj;
  };
  contents.getLinearTicks = function (minValue, maxValue, settings) {
    const linearTicks = [];
    let nearestThousandBase = Math.round(minValue / 1000) * 1000 || 1000;
    const multiplier = settings.maximumAltitudeAllowed === AltitudeOptionsConst.MSL6K.feet
      ? 1
      : settings.maximumAltitudeAllowed === AltitudeOptionsConst.MSL12K.feet
        ? 2
        : 4;
    if (nearestThousandBase > minValue) {
      linearTicks.push(nearestThousandBase);
    }
    while (nearestThousandBase <= maxValue) {
      nearestThousandBase += 1000 * multiplier;
      if (nearestThousandBase <= maxValue) {
        linearTicks.push(nearestThousandBase);
      }
    }
    return linearTicks;
  };
  contents.findDiscount = function (refPrice, actulaPrice, months) {
    if (refPrice && actulaPrice && months) {
      refPrice = parseFloat(refPrice.substr(1));
      actulaPrice = parseFloat(actulaPrice.substr(1));
      const Profit = months * refPrice - actulaPrice;
      return Math.floor((Profit * 100) / (refPrice * months));
    }
    return null;
  };
  contents.getCeilingMinDomain = function (elv) {
    let minValue = 0;
    let i = 0;
    while (i < DefConst.aloftCloudIndexMap.length) {
      if (elv > DefConst.aloftCloudIndexMap[i]) {
        minValue = DefConst.aloftCloudIndexMap[i];
      }
      i += 1;
    }
    return minValue;
  };
  contents.styleTooltip = function (tooltiptext, getTooltipParts) {
    if (tooltiptext) {
      const tooltipParts = tooltiptext.split(':');
      if (tooltipParts.length === 2) {
        if (getTooltipParts) {
          return tooltipParts;
        }
        return `${tooltipParts[0]}: <b>${tooltipParts[1]}</b>`;
      }
    }
    return tooltiptext;
  };
  contents.isMarkerInsidePolygon = function (lat, lng, poly) {
    let polyPoints = poly.getLatLngs();
    if (!polyPoints || polyPoints.length === 0) {
      return null;
    }
    const [xPolyPoint] = polyPoints;
    polyPoints = xPolyPoint;
    const x = lat;
    const y = lng;
    let i = 0;
    let j = polyPoints.length - 1;
    let inside = false;
    let xi;
    let xj;
    let yi;
    let yj;
    let intersect;
    let yiy;
    let yjy;
    while (i < polyPoints.length) {
      xi = polyPoints[i].lat;
      yi = polyPoints[i].lng;
      xj = polyPoints[j].lat;
      yj = polyPoints[j].lng;
      yiy = yi > y;
      yjy = yj > y;
      intersect = yiy !== yjy && x < (xj - xi) * ((y - yi) / (yj - yi)) + xi;
      if (intersect) {
        inside = !inside;
      }
      j = i;
      i += 1;
    }
    return inside;
  };

  function tonum(obj) {
    return parseFloat(obj);
  }

  contents.calcRlh = function (tmp, dew) {
    const a = tonum(tmp); // Air Temperature
    const b = tonum(dew); // Dew Point
    const aC = (5.0 / 9.0) * (a - 32.0);
    const bC = (5.0 / 9.0) * (b - 32.0);
    const c = 6.11 * 10 ** (7.5 * (aC / (237.7 + aC))); // saturation vapor pressure
    const d = 6.11 * 10 ** (7.5 * (bC / (237.7 + bC))); // actual vapor pressure
    const e = (d / c) * 100; // relative humidity
    return Math.round(e * 100) / 100; // Relative Humidity
  };
  contents.dateRangeOverlaps = function (aStart, aEnd, bStart, bEnd) {
    let isDateOverlap = false;
    if (aStart <= bStart && bStart <= aEnd) {
      isDateOverlap = true; // b starts in a
    }
    if (aStart <= bEnd && bEnd <= aEnd) {
      isDateOverlap = true; // b ends in a
    }
    if (bStart < aStart && aEnd < bEnd) {
      isDateOverlap = true; // a in b
    }
    return isDateOverlap;
  };
  contents.sanitizeSearchString = function (search) {
    return replaceAll(search, '[', '');
  };
  contents.convertSMToMeter = function (sm) {
    return sm * 1609.34;
  };
  contents.isValueValid = (value) => value !== null && value !== undefined;
  contents.roundToTenth = function (value, precision) {
    const multiplier = 10 ** precision || 0;
    return Math.round(value * multiplier) / multiplier;
  };

  // only need the timezone information
  // and specifically the first character of every word in timezone
  // using day: 2-digit because otherwise it might include the complete date which is not needed.
  // not using hour: 2-digit might include AM/PM.
  contents.getTimeZoneShort = () => new Date()
    .toLocaleString(undefined, { day: '2-digit', timeZoneName: 'long' })
    .match(/[A-Z]/g)
    .join('');

  contents.roundOfCeiling = function (roundto, value) {
    if (value) {
      const r = value % roundto;
      if (r > 0) {
        return value + (roundto - r);
      }
    }
    return value;
  };
  contents.stopTour = () => {
    const myNode = document.getElementById('tour-container');
    while (myNode.firstChild) {
      myNode.removeChild(myNode.firstChild);
    }
  };
  contents.changeTourElementPosition = () => {
    const myNode = document.getElementById('tour-container');
    while (myNode.firstChild) {
      myNode.removeChild(myNode.firstChild);
    }
    const tourelement = document.getElementsByClassName('v-tour')[0];
    if (tourelement) {
      document.getElementById('tour-container').appendChild(tourelement);
    }
  };
  contents.clearAuditFactory = function (isMapView) {
    // Clean old data keys
    const auditDataKeys = Object.keys(auditDataService);
    if (auditDataKeys) {
      _forEach(auditDataKeys, (item) => {
        if (isMapView && item.indexOf('metars.cache.xml') < 0) {
          delete auditDataService[item];
        } else if (!isMapView) {
          delete auditDataService[item];
        }
      });
    }
  };
  contents.getSliderHeight = () => {
    if (ConfigDeviceSettings.settings.enableAlternateSlider) {
      return AppConst.sliderHeightSecondary;
    }
    if (contents.hideBottomAxis()) {
      return AppConst.sliderHeightSecondary;
    }
    return AppConst.sliderHeight;
  };
  contents.roundCeilings = (value) => {
    if (value < ChartConst.ceilingRoundConfig.l1) {
      value = contents.roundOfCeiling(ChartConst.ceilingRoundConfig.nearestConfig.l1, value);
    } else if (
      value >= ChartConst.ceilingRoundConfig.l1
      && value < ChartConst.ceilingRoundConfig.l2
    ) {
      value = contents.roundOfCeiling(ChartConst.ceilingRoundConfig.nearestConfig.l2, value);
    } else if (value >= ChartConst.ceilingRoundConfig.l2) {
      value = contents.roundOfCeiling(ChartConst.ceilingRoundConfig.nearestConfig.l3, value);
    }
    return value;
  };
  contents.setTourSteps = (vm, key) => {
    let toursteps = HelpBoxConfig[key]?.steps;
    if (!toursteps) {
      return;
    }
    toursteps = toursteps.filter((step) => step.disabled !== true);
    if (vm.hideBottomAxis) {
      toursteps = toursteps.filter((step) => !step.offForLandscape);
    }
    vm.toursteps = toursteps;
  };
  contents.useWhiteImage = (colorValue) => !(colorValue === ChartConst.blackColorIconBackground);
  contents.getDummyMavNode = (forecast_ts) => ({
    forecast_ts,
    tmp: null,
    dpt: null,
    cld: null,
    wdr: null,
    wsp: null,
    p06: null,
    q06: null,
    t06: null,
    st6: null,
    poz: null,
    pos: null,
    typ: null,
    cig: null,
    vis: null,
    obv: null,
    rlh: null,
    xwd: null,
    fzl: null,
    fzc: null,
    fzp: null,
    sno: null,
    frl: null,
  });
  contents.getDummyLampNode = (forecast_ts) => ({
    forecast_ts,
    tmp: null,
    dpt: null,
    cld: null,
    wdr: null,
    wsp: null,
    wgs: null,
    gsp: null,
    xwd: null,
    xwg: null,
    ppo: null,
    lp1: null,
    cp1: null,
    poz: null,
    pos: null,
    typ: null,
    cig: null,
    vis: null,
    obv: null,
    rlh: null,
    fzl: null,
    fzc: null,
    fzp: null,
    sno: null,
    t01: null,
    frl: null,
  });
  contents.clone = function (thing) {
    return contents.deepClone(thing);
  };
  contents.deepClone = function (obj) {
    return !obj || typeof obj !== 'object'
      ? obj
      : underscore.isString(obj)
        ? String.prototype.slice.call(obj)
        : underscore.isDate(obj)
          ? new Date(obj.valueOf())
          : underscore.isFunction(obj.clone)
            ? obj.clone()
            : underscore.isArray(obj)
              ? underscore.map(obj, (t) => contents.deepClone(t))
              : underscore.mapObject(obj, (val) => contents.deepClone(val));
  };
  // contents.deepClone = function (obj) {
  //   return (!obj || (typeof obj !== 'object'))
  //     ? obj
  //     : (_isString(obj))
  //       ? String.prototype.slice.call(obj)
  //       : (_isDate(obj))
  //         ? new Date(obj.valueOf())
  //         : (_isFunction(obj.clone))
  //           ? obj.clone()
  //           : (_isArray(obj))
  //             ? underscore.map(obj, (t) => contents.deepClone(t))
  //             : underscore.mapObject(obj, (val) => contents.deepClone(val));
  // };
  contents.loadBanner = () => {
    const styles = [
      'background: #00000',
      'color: green',
      'display: block',
      'text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3)',
      'line-height: 80px',
      'text-align: center',
      'font-size: 60px',
      'font-weight: bold',
    ];
    const styles2 = [
      'background: #00000',
      'color: green',
      'display: block',
      'text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3)',
      'line-height: 25px',
      'text-align: center',
      'font-size: 20px',
      'font-weight: bold',
    ];
    console.log('%c WeatherSpork', styles.join(';'));
    console.log('%c All rights reserved © 2012, 2020 BuserNet Consulting LLC', styles2.join(';'));
  };

  contents.getCorrectLastViewedStateKey = (lastStateKey) => (lastStateKey === 'app.workshops' || lastStateKey === 'app.avwxtab'
    ? AppConst.defaultHomeViewState
    : lastStateKey);

  contents.roundTimeToNearest30Minutes = (roundedDate = new Date()) => {
    const minutes = roundedDate.getMinutes();
    const roundedMinutes = minutes < 30 ? 0 : 30;
    const copy = new Date(roundedDate);
    copy.setMinutes(roundedMinutes, 0, 0);
    return copy;
  };

  return contents;
};

export default utilityService();
